import type { ExpressionSpecification } from "mapbox-gl"
import type { LayerProps } from "react-map-gl"

/**
 * these nested pills are query params that are comma separated values.
 * These will be split into multiple UI pills
 * @example
 * `?propertyType=House,Condo,Townhouse`
 * `?architecturalStyle=Modern,Traditional,Contemporary`
 */
export const NESTED_PILLS = [
  "propertyType",
  "architecturalStyle",
  "basementFeatures",
  "bodyType",
  "buildingFeatures",
  "commonWalls",
  "constructionMaterials",
  "coolingFeatures",
  "directionFaces",
  "doorFeatures",
  "flooring",
  "foundationDetails",
  "heatingFeatures",
  "interiorFeatures",
  "levels",
  "otherStructures",
  "parkingFeatures",
  "patioAndPorchFeatures",
  "propertyCondition",
  "roof",
  "skirt",
  "structureType",
  "windowFeatures",
  "yearBuiltSource",
  "electric",
  "irrigationSource",
  "powerProductionType",
  "sewer",
  "waterSource",
  "utilities",
  "publicRemarks",
  "hoaAmenities",
  "hoaFeeFrequency",
  "hoaFeeIncludes",
  "existingLeaseType",
  "financialDataSource",
  "incomeIncludes",
  "operatingExpenseIncludes",
  "ownerPays",
  "rentIncludes",
  "farmLandAreaSource",
  "vegetation",
  "appliances",
  "otherEquipment",
  "securityFeatures",
  "availableLeaseType",
  "communityFeatures",
  "currentUse",
  "developmentStatus",
  "fencing",
  "frontageType",
  "furnished",
  "horseAmenities",
  "landLeaseAmountFrequency",
  "laundryFeatures",
  "leaseTerm",
  "lotDimensionsSource",
  "lotFeatures",
  "lotSizeSource",
  "lotSizeUnits",
  "petsAllowed",
  "poolFeatures",
  "possibleUse",
  "roadFrontageType",
  "roadResponsibility",
  "roadSurfaceType",
  "spaFeatures",
  "unitsFurnished",
  "viewFeatures",
  "waterfrontFeatures",
  "taxStatusCurrent",
]

export const COORDINATE_DELIMITER = ":"

/**
 * Bounding box coordinates for the full US map view
 * Format: tl (top-left) and br (bottom-right) coordinates
 */
export const US_MAP_BOUNDING_BOX = {
  topLeft: "-127.65555:61.70404",
  bottomRight: "-65.94314:2.42075",
} as const

/** Dwell time before SRP list hover highlights the matching map pin (avoids flicker when skimming). */
export const SRP_LIST_HOVER_HIGHLIGHT_DELAY_MS = 150

/** Debounce window for high-volume PostHog custom events (slider, map). */
export const POSTHOG_HIGH_FREQUENCY_DEBOUNCE_MS = 400

/** Soften parcel outlines near the min zoom; sharpens fully by z18. */
export const PARCEL_LINE_BLUR_EXPRESSION: ExpressionSpecification = [
  "interpolate",
  ["linear"],
  ["zoom"],
  14,
  0.2,
  18,
  0,
]

export type SRPMapStyle = {
  readonly name: string
  readonly url: string
  readonly icon: string
  readonly parcel: {
    readonly line: {
      /** Ceiling for dynamic `line-opacity` (Satellite uses 1; Streets/Dark use 0.75). */
      readonly maxOpacity: number
      readonly paint: {
        readonly "line-color": string
        readonly "line-width": number
        readonly "line-opacity": number
        readonly "line-blur": number | ExpressionSpecification
      }
    }
  }
  /** Search region boundary (city / zip / etc.) — static; not parcel dynamic opacity/blur. */
  readonly shape: {
    readonly line: {
      readonly paint: {
        readonly "line-color": string
        readonly "line-width": number
        readonly "line-opacity": number
        readonly "line-blur": number
      }
    }
  }
  /** RVM estimate markers — dots (dense) and pills (sparse). */
  readonly rvm: {
    readonly dot: {
      readonly fill: string
      readonly stroke: string
      readonly radius: number
      readonly hoverRadius: number
      readonly strokeWidth: number
    }
    readonly pill: {
      readonly fill: string
      readonly stroke: string
      readonly text: string
    }
    /** When set, overrides `hoverColor` for RVM hover (Dark uses white, not pin pink). */
    readonly hover?: {
      readonly dotFill: string
      readonly pill: {
        readonly fill: string
        readonly stroke: string
        readonly text: string
      }
      readonly tooltip: {
        readonly fill: string
        readonly stroke: string
        readonly text: string
      }
    }
  }
}

export const SRP_MAP_STYLES = [
  {
    name: "Streets",
    url: "mapbox://styles/mapbox/streets-v12",
    icon: "map",
    parcel: {
      line: {
        maxOpacity: 0.75,
        paint: {
          "line-color": "#173F5F",
          "line-width": 1.2,
          "line-opacity": 0,
          "line-blur": PARCEL_LINE_BLUR_EXPRESSION,
        },
      },
    },
    shape: {
      line: {
        paint: {
          "line-color": "#173F5F",
          "line-width": 1,
          "line-opacity": 1,
          "line-blur": 0,
        },
      },
    },
    rvm: {
      dot: {
        fill: "#ffffff",
        stroke: "rgba(15, 23, 42, 0.28)",
        radius: 4,
        hoverRadius: 6,
        strokeWidth: 1.5,
      },
      pill: {
        fill: "#ffffff",
        stroke: "rgba(15, 23, 42, 0.28)",
        text: "#0f172a",
      },
    },
  },
  {
    name: "Satellite",
    url: "mapbox://styles/mapbox/satellite-streets-v12",
    icon: "satellite_alt",
    parcel: {
      line: {
        maxOpacity: 1,
        paint: {
          "line-color": "#FFFFFF",
          "line-width": 1.2,
          "line-opacity": 0,
          "line-blur": PARCEL_LINE_BLUR_EXPRESSION,
        },
      },
    },
    shape: {
      line: {
        paint: {
          "line-color": "#FFFFFF",
          "line-width": 3,
          "line-opacity": 1,
          "line-blur": 0,
        },
      },
    },
    rvm: {
      dot: {
        fill: "#ffffff",
        stroke: "rgba(15, 23, 42, 0.28)",
        radius: 4,
        hoverRadius: 6,
        strokeWidth: 1.5,
      },
      pill: {
        fill: "#ffffff",
        stroke: "rgba(15, 23, 42, 0.28)",
        text: "#0f172a",
      },
    },
  },
  {
    name: "Dark",
    url: "mapbox://styles/mapbox/dark-v11",
    icon: "bedtime",
    parcel: {
      line: {
        maxOpacity: 0.75,
        paint: {
          "line-color": "#FFD54F",
          "line-width": 1.2,
          "line-opacity": 0,
          "line-blur": PARCEL_LINE_BLUR_EXPRESSION,
        },
      },
    },
    shape: {
      line: {
        paint: {
          "line-color": "#FFD54F",
          "line-width": 1,
          "line-opacity": 1,
          "line-blur": 0,
        },
      },
    },
    rvm: {
      dot: {
        fill: "#ffffff",
        stroke: "#ffffff",
        radius: 3.5,
        hoverRadius: 5,
        strokeWidth: 0,
      },
      pill: {
        fill: "rgba(0, 0, 0, 0)",
        stroke: "#ffffff",
        text: "#ffffff",
      },
      hover: {
        dotFill: "#ffffff",
        pill: {
          fill: "rgba(0, 0, 0, 0)",
          stroke: "#ffffff",
          text: "#ffffff",
        },
        tooltip: {
          fill: "#ffffff",
          stroke: "#ffffff",
          text: "#0f172a",
        },
      },
    },
  },
] as const

/** Below this, parcel outline tiles are not drawn. */
export const PARCEL_LAYER_MIN_ZOOM = 14
/** Mapbox vector source id for SRP Regrid parcel MVT tiles. */
export const PARCEL_SOURCE_ID = "parcels-source"
/** Line layer id for SRP parcel outlines (`queryRenderedFeatures` / opacity). */
export const PARCEL_LINE_LAYER_ID = "parcels-layer"
/**
 * At or below this many rendered parcel features, force full line opacity
 * regardless of the zoom curve.
 */
export const PARCEL_FULL_OPACITY_MAX_COUNT = 100
/** Default parcel line opacity ceiling (Streets / Dark). Satellite uses 1. */
export const PARCEL_LINE_DEFAULT_MAX_OPACITY = 0.75
/**
 * Above this many rendered features, use the zoom curve only. Between
 * `PARCEL_FULL_OPACITY_MAX_COUNT` and this value, opacity blends smoothly.
 */
export const PARCEL_DENSITY_BLEND_MAX_COUNT = 250

/**
 * US RVM point tiles (tippecanoe layer `bupi_rvm`), served by
 * `/api/tiles/bupi-rvm/{z}/{x}/{y}`, which Range-reads a single tile out of the
 * PMTiles archive named by `BUPI_RVM_PMTILES_URL`.
 *
 * That URL is deliberately **not** here: it is server-only env read by
 * `src/lib/tilesets.ts`, so it cannot reach a client bundle, and the browser
 * never touches the archive (Mapbox GL 3 has no `addProtocol` for `pmtiles://`).
 * Do not proxy the ~2.1 GB file through Next — the BFF Range-reads single tiles.
 */
export const BUPI_RVM_SOURCE_LAYER = "bupi_rvm"
export const BUPI_RVM_PMTILES_MAX_ZOOM = 16
export const BUPI_RVM_TILE_URL_TEMPLATE = "/api/tiles/bupi-rvm/{z}/{x}/{y}"
/** Shared so `MapContainer` can mark the layer interactive and read clicks off it. */
export const BUPI_RVM_PRICE_LAYER_ID = "bupi-rvm-price-label"
/** Dots at parcel zoom until the in-view count drops under the pill limit. */
export const BUPI_RVM_POINT_LAYER_ID = "bupi-rvm-point"
export const BUPI_RVM_PRICE_HOVER_LAYER_ID = `${BUPI_RVM_PRICE_LAYER_ID}-hover`
/**
 * Counting and dots start here; price pills wait until the in-view count is
 * below `BUPI_RVM_MAX_LABELS_IN_VIEW`. Independent of parcel outline minzoom.
 */
export const BUPI_RVM_MIN_ZOOM = 16
/**
 * At or above this many RVM features, only dots render. Below it the dots
 * convert to price pills. Hovering a dot still reveals the valuation.
 */
export const BUPI_RVM_MAX_LABELS_IN_VIEW = 60

export const LDP_MAP_STYLES = [
  {
    name: "Streets",
    url: "mapbox://styles/mapbox/streets-v12",
    icon: "map",
    easeTo: { pitch: 0, bearing: 0, duration: 1000 },
    layers: [],
    sources: [],
    parcel: {
      fill: { "fill-color": "#007AFF", "fill-opacity": 0.2 },
      line: { "line-color": "#20639B", "line-width": 2 },
    },
  },
  {
    name: "Satellite",
    url: "mapbox://styles/mapbox/satellite-streets-v12",
    icon: "satellite_alt",
    easeTo: { pitch: 0, bearing: 0, duration: 1000 },
    layers: [],
    sources: [],
    parcel: {
      fill: { "fill-color": "#007AFF", "fill-opacity": 0.2 },
      line: { "line-color": "#20639B", "line-width": 2 },
    },
  },
  {
    name: "Dark",
    url: "mapbox://styles/mapbox/dark-v11",
    icon: "dark_mode",
    easeTo: { pitch: 0, bearing: 0, duration: 1000 },
    layers: [],
    sources: [],
    parcel: {
      fill: { "fill-color": "#007AFF", "fill-opacity": 0.2 },
      line: { "line-color": "#FFD700", "line-width": 2 },
    },
  },
] as const
export const LDP_CAMERA_POSITIONS = [
  {
    name: "zoom-in",
    zoom: 17,
    icon: "zoom_in", // material ui symbol
  },
  {
    name: "zoom-out",
    zoom: 12,
    icon: "zoom_out", // material ui symbol
  },
] as const
/** LDP PropertyMap always requests the parcel (zoom-in) static view. */
export const LDP_DEFAULT_CAMERA: (typeof LDP_CAMERA_POSITIONS)[number]["name"] =
  "zoom-in"
export const BPP_PH_EVENTS = {
  // Auth & User Account Events
  AccountMenuComponentOpened: "account:menu_open",
  AccountMenuItemClicked: "account:menu_item_click",
  AccountMenuLinkClicked: "account:menu_link_click",
  AccountMenuToggled: "account:menu_toggle",
  AccountProfileLoadError: "account:profile_load_error",
  AccountProfileLoaded: "account:profile_load",
  AccountProfileUpdateStarted: "account:profile_update_start",
  AccountProfileUpdateError: "account:profile_update_error",
  AccountProfileUpdateSuccess: "account:profile_update_success",
  AccountDeletionStarted: "account:deletion_start",
  AccountDeletionError: "account:deletion_error",
  AccountDeletionCompleted: "account:deletion_complete",
  /** Account landing page CTA (source: hero | returning_users | bottom_cta; workos_flow: signup | login) */
  AccountLandingCTAClicked: "account:landing_cta_click",
  AuthLoginCompleted: "auth:login_complete",
  AuthLogoutCompleted: "auth:logout_complete",
  /** Fired when user clicks a WorkOS OAuth provider button (Google, Apple) before redirect */
  AuthOAuthInitiated: "auth:oauth_initiated",
  AuthPageViewed: "auth:page_view",
  AuthPopupClosed: "auth:popup_close",
  AuthPopupOpened: "auth:popup_open",
  AuthPopupStateChanged: "auth:popup_state_change",
  AuthRedirectToAccount: "auth:redirect_to_account",
  AuthRegisterCompleted: "auth:register_complete",
  AuthStateChanged: "auth:state_change",
  AuthTabChanged: "auth:tab_change",
  LoginError: "auth:login_error",
  LoginStarted: "auth:login_start",
  LoginSuccess: "auth:login_success",
  PartnerClicked: "partner:click",
  PasswordResetRequested: "auth:password_reset_request",
  PasswordResetError: "auth:password_reset_error",
  PasswordResetSuccess: "auth:password_reset_success",
  RegistrationError: "auth:register_error",
  RegistrationStarted: "auth:register_start",
  RegistrationSuccess: "auth:register_success",
  RememberMeToggled: "auth:remember_me_toggle",

  // User Saved Search and Favorite Property events
  SavedSearchClicked: "saved_search:search_click", // Track when a saved search is clicked on the autocomplete results page
  SavedSearchButtonClicked: "saved_search:button_click", // Track when the save search button is clicked
  SavedSearchCreated: "saved_search:search_create", // Track when a saved search is created
  SavedSearchDeleted: "saved_search:search_delete", // Track when a saved search is deleted
  SavedSearchViewed: "saved_search:search_view", // Track when a saved search is viewed
  SavedSearchesOpened: "saved_search:list_open", // Track when the saved searches list view is opened
  PropertyFavoriteAdded: "property:favorite_add", // Track when a property is favorited
  PropertyFavoriteRemoved: "property:favorite_remove", // Track when a property is unfavorited
  PropertyFavoritesOpened: "property:favorites_open", // Track when the property favorites list view is opened
  PropertyFavoriteToggled: "property:favorite_toggle", // Track when a property is favorited or unfavorited
  // Property Events & LDP Events
  PropertyAdditionalDetailsClosed: "property:additional_details_close",
  PropertyAdditionalDetailsOpened: "property:additional_details_open",
  PropertyAdditionalDetailsSectionViewed:
    "property:additional_details_section_view",
  PropertyClicked: "property:card_click",
  MapPinClicked: "map:pin_click",
  PropertyGalleryImageNavigated: "property:gallery_image_navigate",
  PropertyImageClicked: "property:image_click",
  PropertyImageCarouselSwipe: "property:image_carousel_swipe",
  PropertyImageGalleryClosed: "property:gallery_close",
  PropertyImageGalleryOpened: "property:gallery_open",
  PropertyLeadFormClosed: "property:lead_form_close",
  PropertyLeadFormOpened: "property:lead_form_open",
  PropertyLeadFormSubmitted: "property:lead_form_submit",
  PropertyLeadFormTypeChanged: "property:lead_form_type_change",
  PropertyLeadContextRejected: "property:lead_context_rejected",
  PropertyLeadForwarded: "property:lead_forwarded",
  PropertyLeadForwardFailed: "property:lead_forward_failed",
  AgentContactFormContextRejected: "agent:contact_form_context_rejected",
  OfficeContactFormContextRejected: "office:contact_form_context_rejected",
  // Search API rejected our HMAC signature (401 from /v1/forms) — signing
  // misconfig signal (secret mismatch / key id / clock skew). No token/secret/sig logged.
  FormSignatureRejected: "form:signature_rejected",
  PropertyMapCameraChanged: "property:map_camera_change",
  /** Interactive LDP map opened from the static map expand control */
  PropertyMapLightboxOpened: "property:map_lightbox_open",
  PropertyMapStyleChanged: "property:map_style_change",
  PropertyMapTabChanged: "property:map_tab_change",
  PropertyOpenHouseClicked: "property:open_house_click",
  PropertyOpenHouseAddToCalendar: "property:open_house_add_to_calendar",
  PropertyOpenHousesViewed: "property:open_houses_view",
  PropertyPageViewed: "property:page_view",
  PropertyPopupClosed: "property:popup_close",
  PropertyRemarksCollapsed: "property:remarks_collapse",
  PropertyRemarksExpanded: "property:remarks_expand",
  PropertyRoomsClosed: "property:rooms_close",
  PropertyRoomsOpened: "property:rooms_open",
  MortgageCalculatorOpened: "property:mortgage_calculator_open",
  MortgageCalculatorClosed: "property:mortgage_calculator_close",
  PropertySchoolsCollapsed: "property:schools_collapse",
  PropertySchoolsExpanded: "property:schools_expand",
  PropertyPrintClicked: "property:print_click",
  PropertyShareClicked: "property:share_click",
  PropertyTypeChanged: "property:type_change",
  PropertyTypeFilterChanged: "property:type_filter_change",
  PropertyVirtualTourClicked: "property:virtual_tour_click",
  RecentSearchClicked: "search:recent_click", // This event is used to track when a recent search is clicked on the autocomplete results page

  // Filter Events
  BathroomsFilterChanged: "filter:bathrooms_change",
  BedroomsFilterChanged: "filter:bedrooms_change",
  FilterPillCountHover: "filter:pill_count_hover",
  PopularSearchSelected: "search:popular_select",
  PriceFilterChanged: "filter:price_change",
  PriceFilterSliderChanged: "filter:price_slider_change",

  // Search Events & SRP Events
  AutocompleteOpened: "search:autocomplete_open",
  MapStyleChanged: "search:map_style_change", // Track when the map style is changed
  SearchError: "search:error", // TODO: hook this up to the API response error handeling
  SearchFilterPillCountDropdownOpened: "search:filter_pill_count_dropdown_open", // Track when a filter pill count dropdown is opened
  SearchFilterPillRemoved: "search:filter_pill_remove", // Track when a filter pill is removed
  SearchFiltersCleared: "search:filters_clear", // Track when all filters are cleared on tablet and mobile
  SearchFiltersClosed: "search:filters_close", // Track when the search filters on tablet and mobile are closed
  SearchFiltersOpened: "search:filters_open", // Track when the search filters on tablet and mobile are opened
  SearchNearMe: "search:near_me_click", // Track when user clicks to search by their current location
  SearchRequest: "search:request_submit", // MAIN SEARCH EVENT: This tracks search query for all search types (autocomplete, map, filters, smart_filters)
  SearchResultHover: "search:result_hover", // Track when a search result is hovered on the autocomplete results page
  SmartSearchSubmitted: "search:smart_submit", // Track when a smart search is submitted types (keyboard, click, voice, popular searches)
  /** Smart Search entry opened (e.g. homepage lightbox); use `source` property for placement */
  SmartSearchLightboxOpened: "search:smart_lightbox_open",
  SortChanged: "search:sort_change", // Track when a sort option is changed
  VoiceSearchError: "search:voice_error", // Track when a voice search error occurs

  // Misc. Events
  EmailSubmitted: "email:submit", // Track email submission for homepage footer form. Not yet hooked up to the API.
  SocialClicked: "social:click", // Track when a social link is clicked in the footer. It's not yet used.
  FeaturedItemClicked: "featured:item_click", // homepage featured items click event
  LogoClicked: "logo:click", // TODO: hook this up to the Logo component

  // CMS Block Events
  CmsBlockView: "cms:block_view",
  CmsCtaClick: "cms:cta_click",
  CmsLinkClick: "cms:link_click",
  CmsFaqItemToggle: "cms:faq_item_toggle",
  CmsSearchHeroTabChange: "cms:search_hero_tab_change",
  CmsMarketCoverageTabChange: "cms:market_coverage_tab_change",
  CmsFormSubmit: "cms:form_submit",
  CmsPeopleCardClick: "cms:people_card_click",
  CmsBlogPostClick: "cms:blog_post_click",
  CmsFooterLinkClick: "cms:footer_link_click",
  // Polygon Draw Events
  PolygonDrawStart: "polygon:draw_start",
  PolygonDrawComplete: "polygon:draw_complete",
  PolygonCleared: "polygon:cleared",
  PolygonLoadFailure: "polygon:load_failure",
  PolygonSimplificationApplied: "polygon:simplification_applied",
} as const
export const DEFAULT_IMAGE_SRP =
  "https://cribio.b-cdn.net/website-assets/img-property-fallback.webp"

// Property type-specific default images
export const PROPERTY_TYPE_DEFAULT_IMAGES = {
  House:
    "https://cribio.b-cdn.net/website-assets/property-fallback/img-fallback-house.webp",
  Condo:
    "https://cribio.b-cdn.net/website-assets/property-fallback/img-fallback-condo.webp",
  Townhouse:
    "https://cribio.b-cdn.net/website-assets/property-fallback/img-fallback-townhouse.webp",
  Land: "https://cribio.b-cdn.net/website-assets/property-fallback/img-fallback-land.webp",
  // Fallback to general default for property types without specific images
  "Multi-family": DEFAULT_IMAGE_SRP,
  "Co-op": DEFAULT_IMAGE_SRP,
  Commercial: DEFAULT_IMAGE_SRP,
  Mobile: DEFAULT_IMAGE_SRP,
} as const
export const PRICE_CURRENCY_CONFIG: Intl.NumberFormatOptions = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}

export const POPULAR_SEARCHES = [
  "No HOA", // if this changes, we need to update the tests: tests/e2e/smart-filters/popular-search.spec.ts
  "Newly Listed",
  "Built after 2010",
  "More than 1,800 sqft",
  "Mid-century modern architecture",
  "More than 100 days on market",
  "Price Reduced",
  "Price Increased",
]

export const ALLOWED_PILL_PARAM_LIST = [
  "accessibilityFeatures",
  "additionalParcels",
  "ageGte",
  "ageLte",
  "appliances",
  "appointmentRequired",
  "architecturalStyle",
  "areaSource",
  "areaUnits",
  "attachedGarage",
  "availableLeaseType",
  "basement",
  "basementFeatures",
  "bathroomsGte",
  "bathroomsLte",
  "bedroomsGte",
  "bedroomsLte",
  "belowGradeBedroomsGte",
  "belowGradeBedroomsLte",
  "bodyType",
  "buildingFeatures",
  "bupi",
  "businessType",
  "buyerAgentDesignation",
  "buyerFinancing",
  "carport",
  "changeType",
  "city",
  "coBuyerAgentDesignation",
  "coListAgentDesignation",
  "coOpMaintenanceFeeMonthlyGte",
  "coOpMaintenanceFeeMonthlyLte",
  "commonInterest",
  "commonWalls",
  "communityFeatures",
  "compSale",
  "compensationType",
  "concessions",
  "constructionMaterials",
  "constructionMaterialsOrFoundationDetails",
  "cooling",
  "coolingFeatures",
  "coolingOrHeatingFeatures",
  "country",
  "county",
  "cropsIncluded",
  "currentFinancing",
  "currentUse",
  "daysOnMarketGte",
  "daysOnMarketLte",
  "developmentStatus",
  "directionFaces",
  "disabilityAccess",
  "dock",
  "documentStatus",
  "doorFeatures",
  "downPaymentMinimumPercentageGte",
  "downPaymentMinimumPercentageLte",
  "downPaymentResource",
  "dualRateCommission",
  "electric",
  "electricOnProperty",
  "elevator",
  "existingLeaseType",
  "exteriorFeatures",
  "farmCreditService",
  "fencing",
  "fencingFeatures",
  "fhaEligibility",
  "financialDataSource",
  "fireplace",
  "fireplaceFeatures",
  "fixerUpper",
  "flooring",
  "foundationDetails",
  "frontageType",
  "furnished",
  "garage",
  "garageGte",
  "garageLte",
  "grazingPermitsBlm",
  "grazingPermitsForest",
  "grazingPermitsPrivate",
  "greenBuildingVerificationType",
  "greenEnergyEfficient",
  "greenEnergyGeneration",
  "greenIndoorAirQuality",
  "greenSustainability",
  "greenVerification",
  "greenWaterConservation",
  "habitableResidence",
  "heating",
  "heatingFeatures",
  "hoa",
  "hoaAmenities",
  "hoaAmenitiesOrCommunityFeatures",
  "hoaAmenitiesOrIncludes",
  "hoaFeeGte",
  "hoaFeeIncludes",
  "hoaFeeLte",
  "homeWarranty",
  "horse",
  "horseAmenities",
  "hoursDaysOfOperation",
  "ianaTimeZoneValues",
  "idxOfficeParticipation",
  "incomeIncludes",
  "inLawSuite",
  "interiorFeatures",
  "internetAddressDisplay",
  "internetConsumerComment",
  "internetListingDisplay",
  "internetValuationDisplay",
  "irrigationRights",
  "irrigationSource",
  "laborInformation",
  "landLease",
  "landLeaseAmountFrequency",
  "laundryFeatures",
  "leaseAssignable",
  "leaseConsidered",
  "leaseRenewalCompensation",
  "leaseRenewalOption",
  "leaseTerm",
  "levels",
  "linearUnits",
  "listAgentDesignation",
  "listAgentFullName",
  "listOfficeName",
  "listAgentKey",
  "listAgentMlsId",
  "listOfficeKey",
  "listOfficeMlsId",
  "listingAgreement",
  "listingService",
  "listingTerms",
  "listingURLDescription",
  "livingAreaGte",
  "livingAreaLte",
  "lockBoxType",
  "lotDimensionsSource",
  "lotFeatures",
  "lotSizeAcresGte",
  "lotSizeAcresLte",
  "lotSizeGte",
  "lotSizeLte",
  "lotSizeSource",
  "lotSizeSquareMetersGte",
  "lotSizeSquareMetersLte",
  "lotSizeUnits",
  "longTermLease",
  "memberMailOptOut",
  "memberMlsAccess",
  "mlsShort",
  "mobileHomeRemains",
  "newConstruction",
  "occupantType",
  "openHouse",
  "openHouseEndTime",
  "openHouseStartTime",
  "openHouseStartDate",
  "openHouseEndDate",
  "openHouseWeekend",
  "openParking",
  "operatingExpenseIncludes",
  "otherEquipment",
  "otherStructures",
  "ownerPays",
  "ownershipType",
  "parkingFeatures",
  "parkingGte",
  "parkingLte",
  "patioAndPorchFeatures",
  "petsAllowed",
  "pool",
  "poolFeatures",
  "poolPrivate",
  "possession",
  "possibleUse",
  "powerProduction",
  "powerProductionType",
  "preferredPhoto",
  "priceGte",
  "priceIncreaseOrReduction",
  "priceIncreaseOrReductionAmountGte",
  "priceIncreaseOrReductionAmountLte",
  "priceIncreaseOrReductionPercentageGte",
  "priceIncreaseOrReductionPercentageLte",
  "priceLte",
  "pricePerSquareFootGte",
  "pricePerSquareFootLte",
  "propertyAttached",
  "propertyCondition",
  "propertyDst",
  "propertySaleType",
  "propertySubType",
  "propertyType",
  "remarks",
  "rentControl",
  "rentIncludes",
  "roadFrontageType",
  "roadResponsibility",
  "roadSurfaceType",
  "roof",
  "roomType",
  "school",
  "securityFeatures",
  "seniorCommunity",
  "sewer",
  "showingAttended",
  "showingConsiderations",
  "showingContactType",
  "showingRequirements",
  "showingServiceName",
  "signOnProperty",
  "skirt",
  "spa",
  "spaFeatures",
  "specialLicenses",
  "specialListingConditions",
  "standardStatus",
  "stateOrProvince",
  "streetDirection",
  "structureType",
  "syndicateTo",
  "taxStatusCurrent",
  "tenantPays",
  "uniqueListingId",
  "unitGarageAttached",
  "unitTypeType",
  "unitsFurnished",
  "utilities",
  "vegetation",
  "view",
  "viewFeatures",
  "virtualOfficeWebsite",
  "virtualTour",
  "waterSource",
  "waterfront",
  "waterfrontFeatures",
  "windowFeatures",
  "yearBuiltGte",
  "yearBuiltLte",
  "yearBuiltSource",
  "zipcode",
] as const

export type AllowedPillParam = (typeof ALLOWED_PILL_PARAM_LIST)[number]
/**
 * This is a list of all the query parameters that will be used to render the pills.
 *
 * Note: There are other query parameters that are needed in the URL, but should
 * not be used to render the pills: `tl`, `br`, `type`, `typeId`, `sort`, etc...
 *
 * @see {@link NESTED_PILLS}
 */
export const ALLOWED_PILL_PARAMS = new Set<AllowedPillParam>(
  ALLOWED_PILL_PARAM_LIST
)
export const FAVORITES_SAVED_SEARCH_NAME = "favorites" as const
